import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  FilterFn,
  Row,
} from '@tanstack/react-table';
import { ChevronUp, ChevronDown } from 'lucide-react';
import './DataTable.css';

export interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T, unknown>[];
  className?: string;
  /** Enable sorting (default: true) */
  sortable?: boolean;
  /** Initial sorting state */
  initialSorting?: SortingState;
  /** Global filter value */
  globalFilter?: string;
  /** Custom filter function */
  globalFilterFn?: FilterFn<T>;
  /** Row click handler */
  onRowClick?: (row: T) => void;
  /** Custom row className */
  rowClassName?: (row: T) => string;
  /** Empty state message */
  emptyMessage?: string;
  /** Compact mode (smaller padding/font) */
  compact?: boolean;
  /** Show header */
  showHeader?: boolean;
  /** Sticky header */
  stickyHeader?: boolean;
  /** Max height for scrollable body */
  maxHeight?: string;
}

export function DataTable<T>({
  data,
  columns,
  className = '',
  sortable = true,
  initialSorting = [],
  globalFilter,
  globalFilterFn,
  onRowClick,
  rowClassName,
  emptyMessage = 'No data',
  compact = false,
  showHeader = true,
  stickyHeader = false,
  maxHeight,
}: DataTableProps<T>) {
  const [sorting, setSorting] = React.useState<SortingState>(initialSorting);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: sortable ? getSortedRowModel() : undefined,
    getFilteredRowModel: globalFilter ? getFilteredRowModel() : undefined,
    globalFilterFn,
  });

  const tableClasses = [
    'dt-table',
    compact && 'dt-compact',
    stickyHeader && 'dt-sticky-header',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      className="dt-container"
      style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}
    >
      <table className={tableClasses}>
        {showHeader && (
          <thead className="dt-thead">
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id} className="dt-header-row">
                {headerGroup.headers.map(header => {
                  const canSort = sortable && header.column.getCanSort();
                  const sorted = header.column.getIsSorted();

                  return (
                    <th
                      key={header.id}
                      className={`dt-th ${canSort ? 'dt-sortable' : ''} ${sorted ? `dt-sorted-${sorted}` : ''}`}
                      style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    >
                      <div className="dt-th-content">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {canSort && (
                          <span className="dt-sort-icon">
                            {sorted === 'asc' ? (
                              <ChevronUp size={12} />
                            ) : sorted === 'desc' ? (
                              <ChevronDown size={12} />
                            ) : (
                              <span className="dt-sort-placeholder" />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
        )}
        <tbody className="dt-tbody">
          {table.getRowModel().rows.length === 0 ? (
            <tr className="dt-empty-row">
              <td colSpan={columns.length} className="dt-empty-cell">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map(row => {
              const rowData = row.original;
              const customClass = rowClassName ? rowClassName(rowData) : '';

              return (
                <tr
                  key={row.id}
                  className={`dt-row ${customClass} ${onRowClick ? 'dt-clickable' : ''}`}
                  onClick={onRowClick ? () => onRowClick(rowData) : undefined}
                >
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={cell.id}
                      className="dt-td"
                      style={{ width: cell.column.getSize() !== 150 ? cell.column.getSize() : undefined }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

export default DataTable;
